import React, { useEffect } from 'react';
import {
  BottomTabNavigationConfig,
  BottomTabNavigationEventMap,
} from '@react-navigation/bottom-tabs/lib/typescript/src/types';
import {
  BottomTabNavigationOptions,
  BottomTabView,
} from '@react-navigation/bottom-tabs';
import {
  createNavigatorFactory,
  DefaultNavigatorOptions,
  ParamListBase,
  TabActionHelpers,
  TabNavigationState,
  TabRouter,
  TabRouterOptions,
  useNavigationBuilder,
} from '@react-navigation/native';
import { useMaybeNavigationDrawerContext } from '@/contexts/navigation-drawer-context';

export type StatePropagatingNavProps = DefaultNavigatorOptions<
  ParamListBase,
  TabNavigationState<ParamListBase>,
  BottomTabNavigationOptions,
  BottomTabNavigationEventMap
> &
  TabRouterOptions &
  BottomTabNavigationConfig;

/*
 * The DrawerItemList (side nav on tablet + desktop) needs the AppTabs navigation
 * state in order to know which screens it can navigate to. The only way to
 * ensure the nav state is correct is to have the navigator itself report the
 * state up. Otherwise, we can't guarantee that some of our conditional renders
 * (like the ChatStack) will update the navigation state in a way that works
 * with our navigation drawer, and will show no routes as a result.
 *
 * https://gitlab.com/usepepper/product/-/issues/2169
 */
const StatePropagatingView: React.FC<StatePropagatingNavProps> = ({
  children,
  screenListeners,
  screenOptions,
  initialRouteName,
  ...rest
}) => {
  const navigationDrawerContext = useMaybeNavigationDrawerContext();
  const { state, navigation, descriptors } = useNavigationBuilder<
    TabNavigationState<ParamListBase>,
    TabRouterOptions,
    TabActionHelpers<ParamListBase>,
    BottomTabNavigationOptions,
    BottomTabNavigationEventMap
  >(TabRouter, {
    children,
    initialRouteName,
    screenListeners,
    screenOptions,
  });

  useEffect(() => {
    navigationDrawerContext?.setDrawerState(state);
  }, [navigationDrawerContext, state]);

  return (
    <BottomTabView
      {...rest}
      descriptors={descriptors}
      navigation={navigation}
      state={state}
    />
  );
};

const statePropagatingCreator = createNavigatorFactory<
  TabNavigationState<ParamListBase>,
  BottomTabNavigationOptions,
  BottomTabNavigationEventMap,
  typeof StatePropagatingView
>(StatePropagatingView);
export const StatePropagatingStack = statePropagatingCreator();
